
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useNavigate } from "react-router-dom";
import { ContactPerson, ContactRole } from "@/lib/types";
import { useContacts } from "@/hooks/useContacts";
import { contactFormSchema, ContactFormValues } from "./ContactFormSchema";

interface UseContactFormProps {
  contact?: ContactPerson;
  mode: "create" | "edit";
  onSuccess?: (contact: ContactPerson) => void;
}

export function useContactForm({ contact, mode, onSuccess }: UseContactFormProps) {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { useCreateContactMutation, useUpdateContactMutation } = useContacts();
  const createContact = useCreateContactMutation();
  const updateContact = useUpdateContactMutation();

  const form = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: contact
      ? {
          name: contact.name,
          email: contact.email,
          phone: contact.phone || "",
          role: contact.role,
        }
      : {
          name: "",
          email: "",
          phone: "",
          role: "Other" as ContactRole,
        },
  });

  const onSubmit = async (data: ContactFormValues) => {
    setIsSubmitting(true);
    
    try {
      if (mode === "create") {
        const newContact = await createContact.mutateAsync({
          name: data.name,
          email: data.email,
          phone: data.phone || undefined,
          role: data.role as ContactRole,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });
        
        if (onSuccess) {
          onSuccess(newContact);
        } else {
          navigate("/contacts");
        }
      } else if (mode === "edit" && contact) {
        const updatedContact = await updateContact.mutateAsync({
          id: contact.id,
          name: data.name,
          email: data.email,
          phone: data.phone || undefined,
          role: data.role as ContactRole,
          created_at: contact.created_at,
          updated_at: new Date().toISOString()
        });
        
        if (onSuccess) {
          onSuccess(updatedContact);
        } else {
          navigate("/contacts");
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return {
    form,
    isSubmitting,
    onSubmit
  };
}
